import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, MapPin, CheckCircle, XCircle } from 'lucide-react';

export default function AdminLocationsPage() {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null); // null | 'create' | 'edit'
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ name: '' });

  const fetchLocations = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/locations`);
      setLocations(res.data);
    } catch { toast.error('Failed to load locations'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchLocations(); }, []);

  const openCreate = () => { setForm({ name: '' }); setSelected(null); setDialog('create'); };
  const openEdit = (loc) => { setForm({ name: loc.name }); setSelected(loc); setDialog('edit'); };
  const closeDialog = () => { setDialog(null); setSelected(null); };

  // Count active locations to show pricing notice
  const activeLocationCount = locations.filter(l => l.active).length;

  const handleSubmit = async () => {
    if (!form.name.trim()) { toast.error('Location name is required'); return; }
    try {
      if (dialog === 'create') {
        await axios.post(`${API}/locations`, { name: form.name.trim() });
        toast.success('Location created');
      } else {
        await axios.put(`${API}/locations/${selected.id}`, { name: form.name.trim() });
        toast.success('Location updated');
      }
      closeDialog();
      fetchLocations();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error'); }
  };

  const toggleActive = async (loc) => {
    try {
      await axios.put(`${API}/locations/${loc.id}`, { active: !loc.active });
      toast.success(`Location ${loc.active ? 'deactivated' : 'activated'}`);
      fetchLocations();
    } catch (err) { toast.error('Failed to update'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this location? This will affect existing records.')) return;
    try {
      await axios.delete(`${API}/locations/${id}`);
      toast.success('Location deleted');
      fetchLocations();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to delete'); }
  };

  return (
    <div className="max-w-3xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-slate-900">Locations</h1>
          <p className="text-slate-500 text-sm mt-1">Manage your organization's locations</p>
        </div>
        <Button onClick={openCreate} className="gap-2 font-semibold"
          className="btn-primary" style={{ background: '#1D4ED8', color: 'white' }}
          data-testid="admin-locations-create-button">
          <Plus size={14} /> Add Location
        </Button>
      </div>
      <Card className="bg-white shadow-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-slate-100">
                <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Name</TableHead>
                <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Status</TableHead>
                <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Created</TableHead>
                <TableHead className="text-right text-xs font-semibold text-slate-600 uppercase tracking-wide">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                [...Array(3)].map((_, i) => (
                  <TableRow key={i}>
                    {[...Array(4)].map((_, j) => <TableCell key={j}><div className="h-4 bg-slate-100 rounded animate-pulse" /></TableCell>)}
                  </TableRow>
                ))
              ) : locations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="py-12 text-center">
                    <MapPin className="mx-auto mb-2 text-slate-300" size={32} />
                    <p className="text-slate-500">No locations yet</p>
                    <p className="text-slate-400 text-xs mt-1">Add your first location to get started</p>
                  </TableCell>
                </TableRow>
              ) : (
                locations.map(loc => (
                  <TableRow key={loc.id} className="data-row border-slate-50">
                    <TableCell className="font-medium text-slate-800">{loc.name}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Switch checked={loc.active} onCheckedChange={() => toggleActive(loc)} />
                        <Badge className={`text-xs ${loc.active ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-500'} border-0`}>
                          {loc.active ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-slate-500">
                      {loc.created_at ? new Date(loc.created_at).toLocaleDateString() : '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openEdit(loc)}>
                          <Pencil size={13} />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 hover:text-red-500" onClick={() => handleDelete(loc.id)}>
                          <Trash2 size={13} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      <Dialog open={!!dialog} onOpenChange={(o) => !o && closeDialog()}>
        <DialogContent className="bg-white" hideClose={false}>
          <DialogHeader>
            <DialogTitle className="font-heading">{dialog === 'create' ? 'Add Location' : 'Edit Location'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <Label>Location Name</Label>
              <Input
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Downtown Clinic"
                className="bg-white"
                onKeyDown={e => e.key === 'Enter' && handleSubmit()}
              />
            </div>
            {dialog === 'create' && activeLocationCount >= 1 && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200">
                <span className="text-amber-500 text-base flex-shrink-0">💳</span>
                <div>
                  <p className="text-xs font-semibold text-amber-800">Additional location — $29/month</p>
                  <p className="text-xs text-amber-700 mt-0.5">
                    Your first location is included in the base $249/month plan. Each additional active location adds $29/month to your subscription.
                  </p>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" className="bg-white" onClick={closeDialog}>Cancel</Button>
            <Button onClick={handleSubmit}
              className="btn-primary" style={{ background: '#1D4ED8', color: 'white' }}>
              {dialog === 'create' ? 'Create' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
